/**
 * The primitive layer.
 *
 * Hand-built on the shadcn/Radix pattern (composable, variant-driven, styled by
 * class) but with our tokens rather than the stock ones. The brief was explicit
 * that shipping the defaults is not acceptable, and the fastest way to ship
 * them by accident is to scaffold the components in and never look at them.
 *
 * Conventions:
 *   - status colour is semantic, never decorative;
 *   - every interactive element has a visible focus ring (see global.css);
 *   - icons come from lucide, sized to the text they sit beside, and are
 *     `aria-hidden` because the label carries the meaning.
 */

import { cva, type VariantProps } from 'class-variance-authority';
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  ReactNode,
  TdHTMLAttributes,
  ThHTMLAttributes,
} from 'react';
import { cn } from '../../lib/cn';

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

const button = cva(
  'inline-flex items-center justify-center gap-1.5 font-medium whitespace-nowrap ' +
    'transition-colors duration-fast disabled:pointer-events-none disabled:opacity-45',
  {
    variants: {
      variant: {
        // One accent, used only for the single most important action on screen.
        primary: 'bg-accent-600 text-accent-contrast hover:bg-accent-700',
        secondary: 'bg-surface text-ink-900 shadow-hairline hover:bg-surface-sunken',
        ghost: 'text-ink-700 hover:bg-surface-sunken hover:text-ink-900',
        // Destructive is outlined, not filled: a red block reads as an alarm,
        // and escalation is a normal, correct thing to do.
        danger: 'text-stop-700 shadow-hairline hover:bg-stop-100',
      },
      size: {
        sm: 'h-7 rounded-sm px-2.5 text-xs',
        md: 'h-8 rounded px-3 text-sm',
        lg: 'h-9 rounded px-4 text-sm',
      },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof button> {}

export function Button({ className, variant, size, ...props }: ButtonProps) {
  return <button className={cn(button({ variant, size }), className)} {...props} />;
}

// ---------------------------------------------------------------------------
// Badge — the status vocabulary
// ---------------------------------------------------------------------------

const badge = cva(
  'inline-flex items-center gap-1 rounded-sm px-1.5 py-0.5 text-2xs font-semibold ' +
    'whitespace-nowrap leading-none',
  {
    variants: {
      tone: {
        /** A computed fact: verified email, eligibility, a confirmed write. */
        fact: 'bg-ok-100 text-ok-700',
        /** In progress: running, queued, in flight. Not waiting on a person. */
        info: 'bg-info-100 text-info-700',
        /** Held: needs approval, gated, waiting on a human. */
        held: 'bg-hold-100 text-hold-700',
        /** Escalated, blocked, or a dispute. */
        stop: 'bg-stop-100 text-stop-700',
        /** Everything else. Most things. */
        neutral: 'bg-surface-sunken text-ink-500 shadow-hairline',
        /** The active rollout mode, and nothing else. */
        accent: 'bg-accent-50 text-accent-700',
      },
    },
    defaultVariants: { tone: 'neutral' },
  },
);

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement>, VariantProps<typeof badge> {}

export function Badge({ className, tone, ...props }: BadgeProps) {
  return <span className={cn(badge({ tone }), className)} {...props} />;
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('rounded-lg bg-surface shadow-hairline shadow-raised', className)}
      {...props}
    />
  );
}

export function CardHeader({
  title,
  meta,
  action,
}: {
  title: ReactNode;
  meta?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex items-center gap-3 border-b border-line px-4 py-2.5">
      <h2 className="text-sm font-semibold text-ink-900">{title}</h2>
      {meta ? <div className="text-xs text-ink-400">{meta}</div> : null}
      {action ? <div className="ml-auto">{action}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// KPI tile
// ---------------------------------------------------------------------------

export function Stat({
  label,
  value,
  delta,
  direction = 'flat',
  hint,
}: {
  label: string;
  value: string;
  delta?: string;
  direction?: 'up' | 'down' | 'flat';
  hint?: string;
}) {
  // "up" is not automatically good — escalation rate going up is bad. The
  // caller says which direction is favourable by choosing the tone.
  const deltaTone =
    direction === 'up' ? 'text-ok-700' : direction === 'down' ? 'text-stop-700' : 'text-ink-400';

  return (
    <div className="rounded-lg bg-surface px-4 py-3 shadow-hairline">
      <div className="eyebrow">{label}</div>
      <div className="tabular mt-1.5 text-2xl font-semibold text-ink-900">{value}</div>
      {delta ? <div className={cn('mt-0.5 text-xs font-medium', deltaTone)}>{delta}</div> : null}
      {hint ? <div className="mt-0.5 text-xs text-ink-400">{hint}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

export function Table({ className, ...props }: HTMLAttributes<HTMLTableElement>) {
  return (
    <div className="overflow-x-auto">
      <table className={cn('w-full border-collapse text-sm', className)} {...props} />
    </div>
  );
}

// ThHTMLAttributes/TdHTMLAttributes rather than HTMLAttributes: only these
// carry colSpan, rowSpan and scope, and a spanning cell is what an expandable
// row needs.
export function Th({ className, ...props }: ThHTMLAttributes<HTMLTableCellElement>) {
  return (
    <th
      className={cn(
        'eyebrow border-b border-line px-4 py-2 text-left align-bottom font-semibold',
        className,
      )}
      {...props}
    />
  );
}

export function Td({ className, ...props }: TdHTMLAttributes<HTMLTableCellElement>) {
  return (
    <td className={cn('border-b border-line px-4 py-2.5 align-middle', className)} {...props} />
  );
}

// ---------------------------------------------------------------------------
// Meter — used for confidence and rates. Deliberately thin and unfilled-looking
// so it reads as data rather than as a progress bar for something loading.
// ---------------------------------------------------------------------------

export function Meter({ value, label }: { value: number; label: string }) {
  const pct = Math.max(0, Math.min(1, value));
  return (
    <div className="flex items-center gap-2">
      <div
        role="meter"
        aria-valuenow={Math.round(pct * 100)}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
        className="h-1 w-16 overflow-hidden rounded-full bg-surface-sunken shadow-hairline"
      >
        <div
          className={cn('h-full rounded-full', pct >= 0.8 ? 'bg-accent-500' : 'bg-hold-500')}
          style={{ width: `${pct * 100}%` }}
        />
      </div>
      <span className="tabular text-xs text-ink-500">{pct.toFixed(2)}</span>
    </div>
  );
}

